import { CheckCircleIcon, CloudArrowUpIcon } from '@phosphor-icons/react';
import { Upload as AntdUpload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import type { CSSProperties, ReactNode } from 'react';
import { useRef, useState } from 'react';

import { Button } from '@/components/common/button';
import type { UploadFn, UploadMessages, UploadVariant, UploadedFile } from '@/lib/upload';
import { UPLOAD_PRESETS, validateFile } from '@/lib/upload';

import { UploadItem, type UploadEntry } from './upload-item';
import styles from './upload.module.scss';

type AntdUploadRequest = { file: File & { uid: string } };

export type { UploadVariant } from '@/lib/upload';

export type UploadProps = {
  variant: UploadVariant;
  onUpload: UploadFn;
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  maxCount?: number;
  maxSizeMb?: number;
  accept?: string;
  disabled?: boolean;
  helpText?: ReactNode;
  status?: 'error';
  id?: string;
  /** Opt-in per-reason validation copy; each key falls back to the built-in string. */
  messages?: UploadMessages;
  /** Opt-in: show "N out of <maxCount> images|files uploaded". Requires `maxCount`. */
  showCounter?: boolean;
  /** Opt-in: once a file is attached, replace the drop zone with a compact "add more" trigger. */
  collapseWhenFilled?: boolean;
  /** Copy for that trigger. Defaults to "Upload more images" / "Upload more files". */
  addMoreLabel?: ReactNode;
  /** Thumbnails per row in the collapsed image grid. Defaults to 4 (the 464px drawer); the
   *  device form's own frame draws 3. Inert without `collapseWhenFilled`. */
  columns?: number;
  /** Fires whenever the dropzone goes from saying nothing to saying something — holding a file
   *  (uploading or committed) or showing a rejection — and back, so a wrapping `<FormField>` can
   *  suppress its own generic required-field error while a more specific reason is already on
   *  screen (ADO 119326). Not `onChange`, which only ever reflects *committed* files and so can
   *  see neither an in-flight upload nor a refused file. */
  onEntriesChange?: (hasEntries: boolean) => void;
};

type Entry = UploadEntry & { raw?: File; abort?: () => void; committed?: UploadedFile };

const DEFAULT_HELP: Record<UploadVariant, string> = {
  image: UPLOAD_PRESETS.componentImages.helpText,
  document: 'Add a video or upload a PDF/DOC file to help users\nwith installation.',
};

function makePreview(file: File): string | undefined {
  return typeof URL?.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined;
}

export function Upload({
  variant,
  onUpload,
  value,
  onChange,
  maxCount,
  maxSizeMb,
  accept,
  disabled,
  helpText,
  status,
  id,
  messages,
  showCounter,
  collapseWhenFilled,
  addMoreLabel,
  columns,
  onEntriesChange,
}: UploadProps) {
  const [entries, setEntriesState] = useState<Entry[]>(() =>
    (value ?? []).map((file) => ({
      uid: file.id,
      name: file.name,
      size: file.size,
      status: 'success' as const,
      percent: 100,
      url: file.url,
      committed: file,
    })),
  );
  /**
   * Files refused up front by `beforeUpload` — wrong type, too large, over the count.
   *
   * Deliberately **not** `Entry`s (ADO 119326). A refused file is not a file the dropzone holds:
   * it has no thumbnail, no progress, nothing to retry and nothing to remove, so rendering it as
   * a grid tile produced the grey empty square QA reported. It is a statement about the last
   * selection, so it belongs where a field's validation message belongs — one line under the
   * dropzone, in the same red `<FormField>` renders its own error in.
   *
   * Distinct reasons only: picking four oversized files is one problem, not four. A *transport*
   * failure stays an `Entry` — it does concern one named file and carries a Retry.
   */
  const [rejections, setRejections] = useState<string[]>([]);
  const entriesRef = useRef(entries);
  const rejectionsRef = useRef(rejections);
  const hadEntriesRef = useRef(entries.length > 0);

  /** True while anything on this dropzone already explains itself — a held file or a rejection. */
  const announce = (nextEntries: Entry[], nextRejections: string[]) => {
    const hasEntries = nextEntries.length > 0 || nextRejections.length > 0;
    if (hasEntries !== hadEntriesRef.current) {
      hadEntriesRef.current = hasEntries;
      onEntriesChange?.(hasEntries);
    }
  };

  const setEntries = (next: Entry[] | ((prev: Entry[]) => Entry[])) => {
    const resolved = typeof next === 'function' ? next(entriesRef.current) : next;
    entriesRef.current = resolved;
    setEntriesState(resolved);
    announce(resolved, rejectionsRef.current);
  };

  const setRejectionList = (next: string[]) => {
    rejectionsRef.current = next;
    setRejections(next);
    announce(entriesRef.current, next);
  };

  const emit = (next: Entry[]) => {
    onChange?.(next.filter((e) => e.status === 'success' && e.committed).map((e) => e.committed!));
  };

  const patch = (uid: string, changes: Partial<Entry>, andEmit = false) => {
    if (andEmit) {
      const next = entriesRef.current.map((e) => (e.uid === uid ? { ...e, ...changes } : e));
      setEntries(next);
      emit(next);
    } else {
      setEntries((prev) => prev.map((e) => (e.uid === uid ? { ...e, ...changes } : e)));
    }
  };

  const runUpload = (file: File, uid: string) => {
    const controller = new AbortController();
    patch(uid, {
      status: 'uploading',
      percent: 0,
      error: undefined,
      abort: () => controller.abort(),
    });
    onUpload(file, {
      onProgress: (percent) => patch(uid, { percent }),
      signal: controller.signal,
    })
      .then((uploaded) =>
        patch(
          uid,
          {
            status: 'success',
            percent: 100,
            url: uploaded.url,
            name: uploaded.name,
            committed: uploaded,
          },
          true,
        ),
      )
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        patch(uid, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      });
  };

  const handleRequest = (options: AntdUploadRequest) => {
    const file = options.file;
    setEntries((prev) => [
      ...prev,
      {
        uid: file.uid,
        name: file.name,
        size: file.size,
        status: 'uploading',
        percent: 0,
        raw: file,
        retriable: true,
        preview: variant === 'image' ? makePreview(file) : undefined,
      },
    ]);
    runUpload(file, file.uid);
  };

  const reject = (reason: string) => {
    const next = rejectionsRef.current;
    if (!next.includes(reason)) setRejectionList([...next, reason]);
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    // antd calls this once per file; the first of a batch clears whatever the previous selection
    // said, so the message always describes the attempt the operator just made.
    if (fileList[0] === file) setRejectionList([]);

    const err = validateFile(file, { accept, maxSizeMb, messages });
    if (err) {
      reject(err);
      return AntdUpload.LIST_IGNORE;
    }
    if (maxCount) {
      // Position within this batch of *accepted* files — a file rejected earlier in the same
      // multi-select must not push the ones after it over the limit either.
      const batchIndex = fileList
        .filter((f) => !validateFile(f, { accept, maxSizeMb }))
        .indexOf(file);
      const held = entriesRef.current.filter((e) => e.status !== 'error').length;
      if (held + batchIndex >= maxCount) {
        reject(messages?.tooMany ?? `Maximum ${maxCount} files allowed`);
        return AntdUpload.LIST_IGNORE;
      }
    }
    return true;
  };

  const handleRemove = (uid: string) => {
    const target = entriesRef.current.find((e) => e.uid === uid);
    target?.abort?.();
    if (target?.preview) URL.revokeObjectURL(target.preview);
    const next = entriesRef.current.filter((e) => e.uid !== uid);
    setEntries(next);
    emit(next);
  };

  const handleRetry = (uid: string) => {
    const target = entriesRef.current.find((e) => e.uid === uid);
    if (target?.raw) runUpload(target.raw, uid);
  };

  // Attached = uploading + committed. A refused file counts toward nothing — not the counter, not
  // the collapsed layout, not capacity — which is now structural rather than a filter: it never
  // becomes an `Entry` at all (ADO 119326). A *transport* failure is still an entry and is still
  // excluded here, for the reason it always was: three good images plus two failed ones locked the
  // uploader at "3 out of 5" and the operator could never reach five. `beforeUpload` computes the
  // same figure from the ref for the same reason.
  const attached = entries.filter((e) => e.status !== 'error').length;
  const atMax = maxCount ? attached >= maxCount : false;
  const noun = variant === 'image' ? 'images' : 'files';
  const collapsed = Boolean(collapseWhenFilled) && attached > 0;
  const blocked = disabled || atMax;

  const files =
    entries.length > 0 ? (
      <div className={variant === 'image' ? styles.grid : styles.list}>
        {entries.map((entry) => (
          <UploadItem
            key={entry.uid}
            variant={variant}
            entry={entry}
            onRemove={handleRemove}
            onRetry={handleRetry}
          />
        ))}
      </div>
    ) : null;

  // Gated on `attached`, not just `showCounter`: story 112366's AC has the count disappear with the
  // last image rather than sit at "0 out of 5" over an empty drop zone.
  const counter =
    showCounter && maxCount && attached > 0 ? (
      <p className={styles.counter}>
        <CheckCircleIcon size={12} weight="fill" aria-hidden />
        {`${attached} out of ${maxCount} ${noun} uploaded`}
      </p>
    ) : null;

  const rejectionNotice =
    rejections.length > 0 ? (
      <div className={styles.rejections} role="alert">
        {rejections.map((reason) => (
          <p key={reason} className={styles.rejection}>
            {reason}
          </p>
        ))}
      </div>
    ) : null;

  return (
    <div
      className={styles.root}
      id={id}
      style={columns ? ({ '--upload-grid-columns': columns } as CSSProperties) : undefined}
    >
      <AntdUpload.Dragger
        multiple
        accept={accept}
        showUploadList={false}
        disabled={blocked}
        // antd drops role="button"/tabIndex from the drag surface when told a real
        // control lives inside it, so the trigger is the only button here.
        hasControlInside={collapsed}
        beforeUpload={beforeUpload}
        customRequest={handleRequest as (options: object) => void}
        className={[
          styles.dragger,
          collapsed ? styles.draggerFilled : undefined,
          status === 'error' ? styles.error : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {collapsed ? (
          <div className={styles.filled}>
            {files}
            <Button
              variant="outline"
              size="small"
              startIcon={<CloudArrowUpIcon />}
              disabled={blocked}
            >
              {addMoreLabel ?? `Upload more ${noun}`}
            </Button>
            {counter}
          </div>
        ) : (
          <div className={styles.empty}>
            <CloudArrowUpIcon size={32} aria-hidden />
            <p className={styles.help}>{helpText ?? DEFAULT_HELP[variant]}</p>
          </div>
        )}
      </AntdUpload.Dragger>
      {/* Rendered here only when NOT collapsed — the collapsed branch above owns them instead. */}
      {collapsed ? null : files}
      {/* Always below the dropzone, collapsed or not: it is the field's message, not a tile. */}
      {rejectionNotice}
      {collapsed ? null : counter}
    </div>
  );
}
